import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../../hooks/AuthProvider";
import {
  FORMS_FORM_PUBLISH,
  FORMS_FORM_PUBLISH_CHECKS,
  FORMS_FORM_PUBLISH_STATUS,
} from "../../../config/urls";
import { grayFontColor } from "../../../cssUtils/constants";
import typography from "../../../cssUtils/typography";
import ButtonOne from "../../../common/normal-button";
import ButtonTwo from "../../../common/dashboard-button";

const POLL_MS = 3000;
const POLL_MAX_MS = 3 * 60 * 1000;

/**
 * Pre-publish checks, the confirmation, and the wait for Meta's answer.
 * Calls onPublished() once the form is live.
 */
const PublishDialog = ({ open, formId, onClose, onPublished }) => {
  const tenant = localStorage.getItem("tenant");
  const { getValidToken } = useAuth();
  const tokenRef = useRef(getValidToken);
  tokenRef.current = getValidToken;

  // checking | ready | publishing | failed
  const [phase, setPhase] = useState("checking");
  const [checks, setChecks] = useState([]);
  const [canPublish, setCanPublish] = useState(false);
  const [summary, setSummary] = useState(null);
  const [failure, setFailure] = useState(null);
  const [error, setError] = useState("");
  const pollTimer = useRef(null);

  const getHeaders = useCallback(async () => {
    const token = await tokenRef.current();
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };
  }, [tenant]);

  const stopPolling = () => clearTimeout(pollTimer.current);

  const loadChecks = useCallback(async () => {
    setPhase("checking");
    setError("");
    setFailure(null);
    try {
      const response = await fetch(FORMS_FORM_PUBLISH_CHECKS(formId), {
        headers: await getHeaders(),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || "Couldn't run the checks");
      setChecks(data.checks);
      setCanPublish(data.can_publish);
      setSummary(data.summary);
      setPhase("ready");
    } catch (err) {
      setError(err.message || "Couldn't run the checks");
      setPhase("ready");
      setCanPublish(false);
    }
  }, [formId, getHeaders]);

  useEffect(() => {
    if (open) loadChecks();
    return stopPolling;
  }, [open, loadChecks]);

  const poll = useCallback(
    async (startedAt) => {
      try {
        const response = await fetch(FORMS_FORM_PUBLISH_STATUS(formId), {
          headers: await getHeaders(),
        });
        const data = await response.json();
        const result = data.last_result || {};
        if (data.status === "PUBLISHED" || result.state === "succeeded") {
          onPublished();
          return;
        }
        if (result.state === "failed" || (data.status === "DRAFT" && result.state !== "publishing")) {
          setFailure(result);
          setPhase("failed");
          return;
        }
      } catch (err) {
        // Keep waiting; a single failed poll isn't a failed publish.
      }
      if (Date.now() - startedAt > POLL_MAX_MS) {
        setError("Publishing is taking longer than usual. Check View Forms in a few minutes.");
        setPhase("failed");
        return;
      }
      pollTimer.current = setTimeout(() => poll(startedAt), POLL_MS);
    },
    [formId, getHeaders, onPublished],
  );

  const startPublish = async () => {
    setError("");
    try {
      const response = await fetch(FORMS_FORM_PUBLISH(formId), {
        method: "POST",
        headers: await getHeaders(),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (data.checks) {
          setChecks(data.checks);
          setCanPublish(false);
        }
        throw new Error(data?.error || "Couldn't start publishing");
      }
      setPhase("publishing");
      pollTimer.current = setTimeout(() => poll(Date.now()), POLL_MS);
    } catch (err) {
      setError(err.message || "Couldn't start publishing");
    }
  };

  const busy = phase === "checking" || phase === "publishing";

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={typography.h5}>Publish to Meta</DialogTitle>
      <DialogContent>
        {phase === "checking" && (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={28} />
          </Stack>
        )}

        {phase === "publishing" && (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 4 }}>
            <CircularProgress size={28} />
            <Typography sx={typography.bodyLarge}>Publishing to Meta…</Typography>
            <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
              This can take a minute. Please keep this window open.
            </Typography>
          </Stack>
        )}

        {phase === "failed" && (
          <Stack spacing={1.5}>
            <Alert severity="error" sx={{ "& .MuiAlert-message": typography.bodyLarge }}>
              {failure?.message || error || "Publishing failed."}
            </Alert>
            {(failure?.issues || []).map((issue, index) => (
              <Typography key={index} sx={{ ...typography.bodySmall, color: grayFontColor }}>
                • {typeof issue === "string" ? issue : issue.message}
              </Typography>
            ))}
          </Stack>
        )}

        {phase === "ready" && (
          <Stack spacing={1.5}>
            {summary && (
              <Typography sx={typography.bodyLarge}>
                {summary.screens} {summary.screens === 1 ? "screen" : "screens"} ·{" "}
                {summary.fields} {summary.fields === 1 ? "field" : "fields"}
                {summary.categories?.length ? ` · ${summary.categories.length} categor${summary.categories.length === 1 ? "y" : "ies"}` : ""}
              </Typography>
            )}
            {error && (
              <Alert severity="error" sx={{ "& .MuiAlert-message": typography.bodyLarge }}>
                {error}
              </Alert>
            )}
            <Stack spacing={0.75}>
              {checks.map((check) => (
                <Stack key={check.code} direction="row" spacing={1} alignItems="flex-start">
                  {check.ok ? (
                    <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#1FA45A", mt: "2px" }} />
                  ) : (
                    <ErrorOutlineIcon
                      sx={{ fontSize: 18, color: check.blocking ? "#b50000" : "#B7791F", mt: "2px" }}
                    />
                  )}
                  <Box>
                    <Typography sx={typography.bodyLarge}>
                      {CHECK_LABELS[check.code] || check.code}
                    </Typography>
                    {check.message && (
                      <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
                        {check.message}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              ))}
            </Stack>
            <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
              Once published, a form can't be edited and its field keys are locked.
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <ButtonTwo title={phase === "failed" ? "Close" : "Cancel"} onClick={onClose} disabled={busy} />
        {phase === "failed" && <ButtonOne title="Check again" onClick={loadChecks} />}
        {phase === "ready" && (
          <ButtonOne title="Publish" onClick={startPublish} disabled={!canPublish} />
        )}
      </DialogActions>
    </Dialog>
  );
};

const CHECK_LABELS = {
  name: "Form name",
  categories: "Categories",
  saved: "Saved in the builder",
  builder: "Screens and fields",
  waba: "WhatsApp Business account",
};

export default PublishDialog;
